/** Section 7 - the content contract seen by the kernels.
 *
 * Code implements wrenchhands; data instantiates them. Everything here is the
 * shape the kernel reads; authoring schemas and validation live in
 * `@game/content`.
 */

import type {
  ArtifactDefId, BuildingDefId, CreatureDefId, FactionId, HeroDefId,
  SkillDefId, SpellDefId, SubskillDefId,
} from "../ids.js";
import type { Modifier, StatKey, CreatureModifiers } from "../modifiers.js";
import type { ResourceKind, Resources, TerrainKind } from "../state.js";

// ------------------------------------------------------------------ abilities

/** Closed discriminated union (Section 7). New abilities are code, not data. */
export type CreatureAbility =
  | { readonly kind: "FLYING" }
  | { readonly kind: "SHOOTER"; readonly shots: number }
  | { readonly kind: "TWO_HEX" }
  /**
   * Snowhulk Runecarver: carries Expert Runes on its own, and adds to whatever
   * Runes its hero knows. See `battle/runes.ts`.
   */
  | { readonly kind: "RUNE_MASTER" }
  | { readonly kind: "UNDEAD" }
  /**
   * Nothing is alive in there: an elemental, a sentinel, a rookstone, an animated
   * engine. The classic keeps this separate from UNDEAD because the two share only one
   * rule - neither has a mind for a mind spell to reach - and differ on every
   * other: a sentinel is Resurrectable and a skeleton is Destroy-Undead bait.
   */
  | { readonly kind: "NON_LIVING" }
  | { readonly kind: "NO_MELEE_PENALTY" }
  | { readonly kind: "NO_RETALIATION" }
  | { readonly kind: "DOUBLE_ATTACK" }
  | { readonly kind: "EXTRA_RETALIATIONS"; readonly count: number }
  | { readonly kind: "TELEPORT" }
  | { readonly kind: "STRIKE_AND_RETURN" }
  | { readonly kind: "ATTACK_ALL_AROUND" }
  | { readonly kind: "THREE_HEX_BREATH" }
  | { readonly kind: "RANGED_SPREAD"; readonly radiusHexes: number }
  | { readonly kind: "LIFE_DRAIN" }
  | { readonly kind: "RESURRECT_SELF" }
  | { readonly kind: "REGENERATE" }
  | { readonly kind: "AGING" }
  | { readonly kind: "DISEASE"; readonly attackDelta: number; readonly defenseDelta: number }
  /**
   * Acid Breath (Rust Dragon). Every blow eats `defenseDelta` off the target's
   * defence FOR THE REST OF THE BATTLE, and a second blow eats it again.
   *
   * Deliberately not folded into `DISEASE`, which it superficially resembles.
   * Disease is a three-round debuff that does not stack - `stackRule: "first"`
   * - and this is permanent and cumulative, which is the whole of what makes
   * the Rust Dragon frightening: the fourth attack lands on armour that is not
   * there any more. Merging them would need two arguments to say which of the
   * two rules applied, and the two rules would then be one switch arm apart
   * from being two rules again.
   */
  | { readonly kind: "ACID_BREATH"; readonly defenseDelta: number }
  /**
   * Binding (Barkwarden). A stack this one hits may not MOVE until no
   * living binder is standing next to it - it still takes its turn on time, may
   * still strike anything already in reach, and still retaliates.
   *
   * The state is `F2_BOUND`; see that flag for why it is not a speed modifier.
   */
  | { readonly kind: "BINDING" }
  /**
   * Fear. A chance, on every blow, that the target will not strike on its next
   * turn - it may still move, defend and retaliate.
   *
   * The counterpart of `FEARLESS`, which has been in the union since it was
   * written and had nothing to be fearless OF.
   */
  | { readonly kind: "FEAR"; readonly chanceBp: number }
  /**
   * Charm. A chance, on every blow, that the target changes sides for
   * `rounds` - the same machinery Enthrall uses, including its hit-point gate,
   * because an on-hit side-swap with no ceiling would be an instant win against
   * whatever the enemy paid the most for.
   *
   * `maxPool` is that ceiling: total hit points of the target stack, above
   * which the charm simply does not take.
   */
  | { readonly kind: "CHARM"; readonly chanceBp: number; readonly rounds: number; readonly maxPool: number }
  /**
   * Confusion. A chance, on every blow, that the target is driven blood_rage for
   * `rounds`: it attacks whatever is nearest, friend or foe, and its owner does
   * not choose. Same state the Blood Rage spell sets.
   */
  | { readonly kind: "CONFUSION"; readonly chanceBp: number; readonly rounds: number }
  | { readonly kind: "CURSE_ON_HIT" }
  | { readonly kind: "PARALYZE"; readonly chanceBp: number }
  | { readonly kind: "PETRIFY"; readonly chanceBp: number }
  | { readonly kind: "BLIND_ON_HIT"; readonly chanceBp: number }
  | { readonly kind: "POISON"; readonly maxPct: number }
  | { readonly kind: "DISPEL_ON_HIT" }
  | { readonly kind: "MAGIC_RESISTANCE"; readonly pct: number }
  | { readonly kind: "MAGIC_IMMUNITY"; readonly maxSpellLevel: number }
  | { readonly kind: "SPELL_IMMUNITY"; readonly spells: readonly SpellDefId[] }
  | { readonly kind: "SPELL_VULNERABILITY"; readonly spells: readonly SpellDefId[]; readonly mulFp: number }
  /**
   * A spell the creature may cast itself, spending its turn to do it.
   *
   * `casts` is the battle-long budget and `cooldownRounds` the pacing, and they
   * answer different questions: three casts says how much of this a stack ever
   * gets, two rounds says it cannot be spent all at once. A creature may carry
   * SEVERAL of these - the enumerator offers each - and they share one budget
   * and one cooldown, so two spells is a choice about which to spend rather
   * than twice the magic. `cooldownRounds` is optional because every caster
   * shipped before it had none, and absent means "as often as the budget
   * allows", which is what those creatures already did.
   */
  | {
      readonly kind: "CASTER";
      /**
       * Everything this entry lets the creature cast, sharing ONE budget. A
       * list and not a single spell because "knows six spells" and "casts six
       * times" are different claims and the shape has to keep them apart:
       * `castsForCreature` sums the entries, so a Windcaller authored as six
       * one-spell entries would carry six casts whether anyone meant it to or
       * not. One entry, six spells, `casts: 3` says what a Windcaller actually is.
       * `CAST_AFTER_SHOT` has always taken a list for the same reason.
       */
      readonly spells: readonly SpellDefId[];
      readonly casts: number;
      readonly power: number;
      readonly cooldownRounds?: number | undefined;
    }
  /**
   * A blow the creature charges with magic: harder than its own, and blunted
   * by whatever the target has against that school.
   *
   * It is a CHOICE and not a property of every swing. `uses` is what makes it
   * one - "is this target worth spending one on" is a decision, where "which
   * of these two numbers is larger" is arithmetic the player would simply
   * always get right. The same reason `casts` bounds a caster.
   *
   * `bonusPct` can be bold precisely because `uses` is small: a permanent +25%
   * has to be timid, three blows at +60% can be read off a fight and judged.
   */
  | {
      readonly kind: "MAGIC_ATTACK";
      readonly school: SpellSchool;
      readonly bonusPct: number;
      readonly uses: number;
      readonly cooldownRounds?: number | undefined;
    }
  | { readonly kind: "HATES"; readonly creature: CreatureDefId; readonly bonusPct: number }
  | { readonly kind: "MORALE_AURA"; readonly delta: number }
  | { readonly kind: "LUCK_AURA"; readonly delta: number }
  | { readonly kind: "REDUCE_ENEMY_MORALE"; readonly delta: number }
  | { readonly kind: "REDUCE_ENEMY_LUCK"; readonly delta: number }
  | { readonly kind: "JOUSTING"; readonly pctPerHex: number }
  | { readonly kind: "DEATH_STARE"; readonly chanceBp: number }
  | { readonly kind: "DEATH_CLOUD" }
  /**
   * Detonation (Clockwork Guard). On death every ADJACENT stack, friend or foe,
   * takes `DETONATION_BASE + DETONATION_PER_HEAD * n` where `n` is the count
   * standing before the fatal blow. The expansion states it plainly: the damage is not
   * touched by any conventional or magic modifier, so it is dealt flat.
   */
  | { readonly kind: "DETONATION" }
  /**
   * Preemptive shot (Pistoleer). When a shooter takes aim at this stack, it
   * fires first. `perRound` is how many times it may do so - The expansion gives the
   * Pistoleer one a round and the Bounty Pistoleer no limit, which is `0` here.
   */
  | { readonly kind: "PREEMPTIVE_SHOT"; readonly perRound: number }
  /**
   * Meditation (Sun Serpent). Once a battle, instead of moving: the stack cannot be
   * attacked, takes no damage and shrugs off hostile spells until its next
   * turn begins. A turn spent buying a turn.
   */
  | { readonly kind: "MEDITATION" }
  /**
   * Devours corpses (Dune Devourers). Eat a body instead of acting and bank an
   * extra strike; they stack without limit. See `battle/devour.ts`.
   */
  | { readonly kind: "DEVOUR_CORPSES" }
  /**
   * Accurate shot (Old Salt): a ranged Death Stare. Same roll and the same
   * "kills the top creature outright" effect, but only on a SHOT - which is
   * what separates it from `DEATH_STARE`, a melee ability.
   */
  | { readonly kind: "ACCURATE_SHOT"; readonly chanceBp: number }
  /**
   * Ferocity (Squall Hawk): a blow that kills at least one creature is followed by
   * a second blow. Conditional double attack, not an unconditional one.
   */
  | { readonly kind: "FEROCITY" }
  /**
   * Revenge (Abyssal Coil): damage rises as the stack is worn down, by the expansion's own
   * formula in `battle/revenge.ts`. Zero at full strength by construction.
   */
  | { readonly kind: "REVENGE" }
  /**
   * Heat Stroke (Steam Colossus, Juggernaut): an action spent instead of moving
   * or attacking that cooks everything in a fan ahead of the machine, friend
   * and foe alike, with no retaliation. See `battle/heatstroke.ts`.
   */
  | { readonly kind: "HEAT_STROKE" }
  /**
   * Cast after shot (Brine Hexer, Sorceress). Having shot, the stack lays a spell
   * on what it hit - free, costing no cast and no action.
   *
   * `spells` is tried IN ORDER and the first one the target is not already
   * under is the one that lands. The expansion states it exactly that way for the Sea
   * Witch: Weakness, or Sundering Ray if Weakness is already on. Content
   * names the order; the rule only walks the list.
   */
  | { readonly kind: "CAST_AFTER_SHOT"; readonly spells: readonly SpellDefId[]; readonly power: number }
  /**
   * Freezing shot (Frost Oracle): a SHOT that may leave the target frozen -
   * `chanceBp` to lose its turns for `rounds`. Ranged only, which is what
   * separates it from `PARALYZE`; and it reaches the mindless, because ice is
   * not a mind spell.
   */
  | { readonly kind: "FREEZING_SHOT"; readonly chanceBp: number; readonly rounds: number }
  /**
   * Ignores `pctBp` basis points of the attacker's attack value when defending
   * (Reef Guard 30%, Reef Champion 60%). The mirror of `DEFENSE_PIERCE`, read off the
   * DEFENDER and applied to the attack that enters the bracket.
   */
  | { readonly kind: "ATTACK_IGNORE"; readonly pctBp: number }
  /**
   * Repairing (Wrenchhand, Engineer). Heals `hpPerUnit` per creature to a
   * MECHANICAL stack - The expansion lets it mend war machines and Clockwork Guards, which
   * ordinary healing will not touch.
   */
  | { readonly kind: "REPAIR"; readonly hpPerUnit: number }
  /**
   * Ranged attacks ignore `pctBp` basis points of the target's defence
   * (Pebble Bombardier: 20%). Melee is unaffected.
   */
  | { readonly kind: "DEFENSE_PIERCE"; readonly pctBp: number }
  /** Luck this creature brings itself, and nobody else (Pebbleshot). */
  | { readonly kind: "SELF_LUCK"; readonly delta: number }
  | { readonly kind: "FIRE_SHIELD"; readonly pct: number }
  | { readonly kind: "FEARLESS" }
  | { readonly kind: "UNLIMITED_RETALIATION" }
  | { readonly kind: "SIEGE_MACHINE" }
  /**
   * This machine may aim at masonry as well as at troops.
   *
   * The catapult gets it from its shape - a siege machine that deals damage and
   * has no bow can do nothing else - so the ability exists for the machine that
   * is BOTH, the Cogworks's Cannon: a gun that shoots the army and can turn on
   * the wall. Anything without `SIEGE_MACHINE` ignores it.
   */
  | { readonly kind: "WALL_BREAKER" }
  | { readonly kind: "SUMMONER"; readonly creature: CreatureDefId }
  /**
   * School (Blackwater). The stack is part of a wet-native pack: if three or
   * more SCHOOL stacks of the same side are adjacent at the start of its turn,
   * it gets +1 Attack and ignores 20% of the target's Defence. Also the marker
   * that this stack flops on dry hexes and treats wet hexes as home.
   */
  | { readonly kind: "SCHOOL" }
  /**
   * Fights better in company: extra Attack while another stack of the SAME
   * creature is standing next to it.
   *
   * A wolf pack is the case it was written for, and it is deliberately keyed on
   * the creature rather than on the side - two Fang Riders together are a pack,
   * a Fang Rider next to a Scrapper is a wolf and a scrapper. It is the first
   * ability whose value depends on where a player puts a stack, which is the
   * whole reason for it.
   */
  | { readonly kind: "PACK_BOND"; readonly attack: number }
  /**
   * Grows on what it kills: Attack for every creature it has killed this
   * battle, up to a cap.
   *
   * Capped because it compounds - each point of Attack buys damage, which buys
   * kills, which buys Attack - and an uncapped version turns one lucky opening
   * blow into an unanswerable stack by round four.
   */
  | { readonly kind: "SOUL_HARVEST"; readonly perKill: number; readonly cap: number }
  /**
   * An ability the player spends the stack's turn on, with a cooldown.
   *
   * Every other ability in this union is passive or triggered: it reads the
   * board and pays out on its own, and the roster it describes is the same
   * roster every turn. This is the one that is a decision. `cooldownRounds` is
   * what keeps it from being a rotation - two rounds is the floor - and the
   * turn it costs is what keeps it from being free.
   */
  | {
      readonly kind: "ACTIVATED";
      readonly effect: "fortify" | "warcry" | "venom" | "terrify";
      readonly cooldownRounds: number;
      readonly power: number;
    };

// ----------------------------------------------------------------- creatures

export interface CreatureDef {
  readonly id: CreatureDefId;
  readonly name: string;
  readonly namePlural: string;
  readonly faction: FactionId;
  readonly tier: 1 | 2 | 3 | 4 | 5 | 6 | 7;
  readonly upgraded: boolean;
  readonly upgradeOf: CreatureDefId | null;
  readonly attack: number;
  readonly defense: number;
  readonly damageMin: number;
  readonly damageMax: number;
  readonly hp: number;
  readonly speed: number;
  readonly growth: number;
  readonly aiValue: number;
  readonly cost: Partial<Resources>;
  readonly abilities: readonly CreatureAbility[];
  /** Asset key into the sprite manifest. */
  readonly art: string;
}

// -------------------------------------------------------------------- spells

export type SpellSchool = "air" | "earth" | "fire" | "water" | "all";
export type SpellTargeting =
  | "SELF" | "SINGLE_ALLY" | "SINGLE_ENEMY" | "SINGLE_ANY"
  | "ALL_ALLIES" | "ALL_ENEMIES" | "ALL" | "HEX" | "AREA" | "MAP" | "NONE";

export interface SpellEffectSpec {
  readonly kind:
    | "DAMAGE" | "HEAL" | "RESURRECT" | "MODIFIER" | "SUMMON" | "DISPEL"
    | "TELEPORT" | "CLONE" | "SACRIFICE" | "ADVENTURE";
  /** Damage or heal per point of spell power. */
  readonly perPower?: number;
  readonly flat?: number;
  readonly modifiers?: readonly Modifier[];
  readonly summon?: CreatureDefId;
  readonly durationRounds?: number;
  readonly adventure?: string;
}

export interface SpellDef {
  readonly id: SpellDefId;
  readonly name: string;
  readonly level: 1 | 2 | 3 | 4 | 5;
  readonly school: SpellSchool;
  readonly combat: boolean;
  readonly adventure: boolean;
  readonly cost: readonly [number, number, number, number]; // by mastery 0..3
  readonly targeting: SpellTargeting;
  readonly effects: readonly SpellEffectSpec[];
  /** Extra effect gained at expert mastery, if any. */
  readonly expertBonus?: SpellEffectSpec;
  /**
   * One of the classic's mind spells - Blood Rage, Enthrall, Blind, Befuddle, Frenzy,
   * Mirth, Sorrow. They pass straight through anything without a mind, which is
   * a property of the *spell*, so it is a flag here rather than a list of seven
   * spell ids repeated on every mindless creature in the game.
   */
  readonly mind?: boolean;
  /**
   * Expert mastery casts this on the whole side instead of one stack, which is
   * how the classic spells Mass Bless, Mass Slow and the rest. It only means anything on
   * SINGLE_ALLY and SINGLE_ENEMY spells; `resolveTargets` reads it.
   */
  readonly massAtExpert?: boolean;
  /**
   * Carried, never taught: no guild, no research, no shrine, no scholar. The
   * only way into a spellbook is an artifact's `grantsSpells`.
   */
  readonly artifactOnly?: boolean;
  /**
   * A bolt that jumps: `targets` is the most stacks one cast may touch and
   * `falloffFp` the fixed-point share of the damage each jump keeps, so Chain
   * Lightning is `{ targets: 4, falloffFp: 500 }`. Absent on every other spell.
   */
  readonly chain?: { readonly targets: number; readonly falloffFp: number };
  readonly art: string;
  readonly description: string;
}

// ----------------------------------------------------------------- artifacts

export type ArtifactSlot =
  | "head" | "neck" | "torso" | "shoulders" | "leftHand" | "rightHand"
  | "leftRing" | "rightRing" | "feet" | "misc1" | "misc2" | "misc3" | "misc4" | "misc5"
  | "warMachine1" | "warMachine2" | "warMachine3" | "warMachine4" | "spellbook";

export type ArtifactClass = "treasure" | "minor" | "major" | "relic" | "combination" | "special";

export interface ArtifactDef {
  readonly id: ArtifactDefId;
  readonly name: string;
  readonly slot: ArtifactSlot;
  readonly rank: ArtifactClass;
  readonly cost: number;
  readonly modifiers: readonly Modifier[];
  /** Components, for combination artifacts. */
  readonly components: readonly ArtifactDefId[];
  /**
   * Spells this artifact puts in its wearer's spellbook while it is worn.
   *
   * The one thing an artifact could not express before: `modifiers` can change
   * any number in the game and cannot add a verb. Colossus Storm had promised
   * the Colossus Thunderbolt in prose since it was authored.
   */
  readonly grantsSpells?: readonly SpellDefId[];
  readonly art: string;
  readonly description: string;
}

// -------------------------------------------------------------------- skills

/**
 * One branch of a secondary skill: a modern remake's subskill.
 *
 * A skill's three rungs are the same for everyone; a subskill is the part the
 * player chooses. Crossing into advanced offers `subskills.advanced` and
 * crossing into expert offers `subskills.expert`, one pick each, and the picked
 * modifiers ride alongside the rung's own for as long as the hero holds it.
 *
 * No `art` of its own. A subskill is read inside its parent skill's plate and
 * shows the parent's icon, which is also the only shape that ships without
 * drawing five new 44px tiles per skill.
 */
export interface SubskillDef {
  readonly id: SubskillDefId;
  readonly name: string;
  readonly modifiers: readonly Modifier[];
  readonly description: string;
}

/**
 * The branches a skill offers, by the rung that opens them.
 *
 * Empty arrays are the default and mean "this skill has no branch yet": every
 * skill authored before subskills existed loads as two empty pools and behaves
 * exactly as it did, which is what lets the table be filled in one skill at a
 * time rather than all twenty-eight at once.
 */
export interface SubskillChoices {
  readonly advanced: readonly SubskillDef[];
  readonly expert: readonly SubskillDef[];
}

export interface SecondarySkillDef {
  readonly id: SkillDefId;
  readonly name: string;
  /** Modifiers granted at basic / advanced / expert. */
  readonly levels: readonly [readonly Modifier[], readonly Modifier[], readonly Modifier[]];
  readonly art: string;
  readonly description: readonly [string, string, string];
  readonly subskills: SubskillChoices;
}

/** The rung a level opens a pick on: advanced at 2, expert at 3, none at 1. */
export function subskillRung(level: number): keyof SubskillChoices | null {
  return level >= 3 ? "expert" : level >= 2 ? "advanced" : null;
}

/** A skill's subskill by id, from either rung, or undefined. */
export function subskillById(
  def: SecondarySkillDef, id: SubskillDefId,
): SubskillDef | undefined {
  for (const rung of SUBSKILL_RUNGS) {
    for (const sub of def.subskills[rung]) if (sub.id === id) return sub;
  }
  return undefined;
}

/** The subskills a hero actually took, in pick order, skipping unknown ids. */
export function pickedSubskills(
  def: SecondarySkillDef, picks: readonly SubskillDefId[],
): readonly SubskillDef[] {
  const out: SubskillDef[] = [];
  for (const id of picks) {
    const sub = subskillById(def, id);
    if (sub !== undefined) out.push(sub);
  }
  return out;
}

/** Both rungs in the order a hero climbs them. */
export const SUBSKILL_RUNGS: readonly (keyof SubskillChoices)[] =
  Object.freeze(["advanced", "expert"]);

// ------------------------------------------------------------------ buildings

export interface BuildingDef {
  readonly id: BuildingDefId;
  readonly faction: FactionId;
  readonly name: string;
  /** Town-screen slot; every building in an upgrade chain shares one. */
  readonly slot: string;
  readonly cost: Partial<Resources>;
  readonly requires: readonly BuildingDefId[];
  readonly upgradeOf: BuildingDefId | null;
  /** Dwelling buildings make this creature available. */
  readonly dwellingFor: CreatureDefId | null;
  readonly modifiers: readonly Modifier[];
  /** Mage guild level granted, 0 for none. */
  readonly guildLevel: number;
  /**
   * Artifacts this building offers over the counter, at their own gold cost.
   * It is what makes a Blacksmith a shop rather than a decoration: a hero
   * standing in the town buys a war machine here. Empty for most buildings.
   */
  readonly sells: readonly ArtifactDefId[];
  /**
   * Bonuses this building gives to NAMED CREATURES rather than to the hero.
   *
   * A building's `modifiers` land on a hero, a town or a field - which is the
   * right home for a Lighthouse or a Brimstone Cloud, and the wrong one for
   * Saltreach's Guiding Star, whose +1 attack belongs to the Reef Guards and not to whoever
   * is carrying them. Reuses the `CreatureModifiers` shape hero specialties
   * already use, so `startBattle` needs no second path: both arrive in
   * `BattleHeroInput.creatureModifiers` and are installed by the same code.
   *
   * Empty for almost every building.
   */
  readonly creatureBonuses: readonly CreatureModifiers[];
  readonly art: string;
  readonly description: string;
}

// -------------------------------------------------------------------- heroes

export interface HeroDef {
  readonly id: HeroDefId;
  readonly name: string;
  readonly faction: FactionId;
  readonly heroClass: string;
  readonly might: boolean;
  readonly startingPrimary: Readonly<Record<"attack" | "defense" | "spellPower" | "knowledge", number>>;
  readonly startingSkills: readonly { readonly def: SkillDefId; readonly level: 1 | 2 | 3 }[];
  readonly startingSpell: SpellDefId | null;
  readonly startingArmy: readonly { readonly def: CreatureDefId; readonly min: number; readonly max: number }[];
  readonly specialty: { readonly kind: string; readonly target: string; readonly modifiers: readonly Modifier[] };
  readonly portrait: string;
}

// ------------------------------------------------------------------- factions

export interface FactionDef {
  readonly id: FactionId;
  readonly name: string;
  readonly nativeTerrain: TerrainKind;
  readonly creatures: readonly CreatureDefId[];
  readonly buildings: readonly BuildingDefId[];
  /** Mage-guild spell distribution, in basis points, indexed by school. */
  readonly guildBias: Readonly<Record<SpellSchool, number>>;
  readonly townArt: string;
  readonly mightClass: string;
  readonly magicClass: string;
  /**
   * Walk ocean tiles in land mode when the whole army is this faction. Mixed
   * armies still need a boat. Absent means never.
   */
  readonly amphibious?: boolean;
  /**
   * River overlay cells cost as a dirt road when both ends of the step are
   * rivered. Blackwater's strategic hook; absent means rivers are paint.
   */
  readonly riverAsRoad?: boolean;
  /**
   * Cap every road step at this cost (Castle: cobble 50). Absent means the
   * worse of the two tiles, as usual.
   */
  readonly roadCostCap?: number;
  /** Extra terrains treated as native (cost never above a plain grass step). */
  readonly extraNative?: readonly TerrainKind[];
  /** Absolute off-road costs that replace the terrain table for those kinds. */
  readonly terrainCosts?: Readonly<Partial<Record<TerrainKind, number>>>;
  /** River kinds that cost as a road when both ends of the step are that kind. */
  readonly riverRoadKinds?: readonly ("water" | "ice" | "lava" | "mud")[];
  /** Cost of `riverRoadKinds` steps. Defaults to a dirt road. */
  readonly riverRoadCost?: number;
  /** Underground (z > 0) walkable terrain costs as a plain grass step. */
  readonly underworldNative?: boolean;
  /** May enter a water cell that touches land. Fenmire mireboats. */
  readonly shoreWalk?: boolean;
  /**
   * When one of our stacks falls, the rest of the line closes ranks: every
   * surviving stack of ours gains this much morale for the rest of the battle,
   * capped at the classic's +3. Replaced Castle's oath, which forbade retreat and
   * surrender - a rule that cost the computer every hero it could have saved.
   */
  readonly rally?: { readonly morale: number };
  /** First ranged hit on each of our stacks this round is halved, on this terrain. */
  readonly canopy?: { readonly terrain: TerrainKind };
  /** Adjacent non-living pack of at least `pack` heals `heal` at round start. */
  readonly lattice?: { readonly pack: number; readonly heal: number };
  /** Paint fire hexes on these battle terrains. */
  readonly cinders?: { readonly terrains: readonly TerrainKind[] };
  /** Chance to raise `creature` when a living enemy is killed this battle. */
  readonly wake?: { readonly chanceBp: number; readonly creature: CreatureDefId };
  /** Extra tactics columns when the fight is in cavern or unexplored fog. */
  readonly blindsideHexes?: number;
  /** The stack that scores the first kill of the battle gains this Attack. */
  readonly blooding?: { readonly attack: number };
  /** Paint poison hexes on this battle terrain. */
  readonly miasma?: { readonly terrain: TerrainKind };
  /**
   * If the army fields at least one stack from each group, enemy magic
   * resistance is halved.
   */
  readonly attunement?: { readonly groups: readonly (readonly CreatureDefId[])[] };
  /** First melee blow each stack lands in a water/boat fight ignores this much Defence. */
  readonly boarding?: { readonly defIgnoreBp: number };
  /** Non-living stacks and war machines gain Speed and a heal at round start. */
  readonly overclock?: { readonly speed: number; readonly heal: number };
  /** A stack that Waited or Defended last round gains this Defence this round. */
  readonly shieldWall?: { readonly defense: number };
  /**
   * The faction's one activated racial, spent on a stack's turn and available
   * once per battle per side (H5's racial abilities).
   *
   * Twelve of the perks above it are passive - they read the board and pay out
   * on their own. This is the other kind: a button, a charge, and a decision
   * about when to press it. Every one of them is priced the same way, which is
   * what keeps the union from becoming a grab bag - one charge, the active
   * stack's whole turn, and no permanent army out of any of them. The one
   * faction that may create army permanently is Gravewatch, and it already
   * does, through Gravecalling, which is rate-limited by a skill.
   */
  readonly racial?:
    /**
     * Emberpit. A weaker duplicate of the stack steps onto the field and stays
     * for the fight. It is `F_SUMMONED`, so it is never folded back into the
     * army: what the gate buys is this battle and nothing after it.
     */
    | { readonly kind: "gate"; readonly pctBp: number }
    /**
     * Ironhorde. Every living stack on the side gains Attack, and gains more
     * of it for every stack the side has already lost - the comeback half of
     * H5's blood rage, without the per-stack damage bookkeeping.
     */
    | { readonly kind: "rage"; readonly attack: number; readonly perFallen: number; readonly cap: number }
    /**
     * Deepvault. The stack gives up a share of itself and the hero gets mana back
     * for it, priced in hit points rather than in heads so a hundred scrappers
     * are not a cheaper sacrifice than two dragons.
     */
    | { readonly kind: "ritual"; readonly costPctBp: number; readonly hpPerMana: number }
    /**
     * Castle. The stack fights the rest of this battle as its upgraded form -
     * H5's Haven Training. The ARMY keeps the creature it brought: the fold-back
     * writes survivor counts against the army's own slot defs, so what this buys
     * is one battle, not a free upgrade.
     */
    | { readonly kind: "train" }
    /** Greenmantle. Every living stack gains Speed for the rest of the battle. */
    | { readonly kind: "swiftness"; readonly speed: number }
    /**
     * Tower. Mana back, and the round's cast handed back with it, so the hero
     * may cast twice in one round - once. The one charge is the whole limit.
     */
    | { readonly kind: "channel"; readonly manaPerKnowledge: number }
    /**
     * Gravewatch. The dead of this battle stand up: a temporary stack whose
     * count grows with what the side has killed. `F_SUMMONED`, so it is never
     * folded back - the permanent raising is Gravecalling's, where a skill
     * rate-limits it.
     */
    | {
        readonly kind: "raise";
        readonly creature: CreatureDefId;
        readonly perKill: number;
        readonly cap: number;
      }
    /** Fenmire. Every enemy stack takes this share of its own hit points as
     *  poison, for the battle. */
    | { readonly kind: "plague"; readonly hpPctBp: number }
    /** Prismgate. Elemental protection on the whole line, every school at once. */
    | { readonly kind: "attune"; readonly resistFp: number; readonly rounds: number }
    /** Saltreach. Every shooter's ammunition, back. */
    | { readonly kind: "press"; readonly shots: number }
    /** Cogworks. The mechanical half of the army is repaired and re-armed. */
    | { readonly kind: "overhaul"; readonly heal: number; readonly shots: number }
    /** Frostmark. Rune levels handed to the whole line at once. */
    | { readonly kind: "runegift"; readonly levels: number }
    /** Blackwater. More of the board goes under water, which is where their
     *  pack fights best. */
    | { readonly kind: "flood"; readonly hexes: number };
}

// -------------------------------------------------------------- map objects

export interface MapObjectDef {
  readonly id: string;
  readonly kind: string;
  readonly art: string;
  /** Footprint in cells, relative to the anchor at the bottom-left. */
  readonly footprint: readonly (readonly [number, number])[];
  /** Cells within the footprint the hero may step on to trigger it. */
  readonly entrances: readonly (readonly [number, number])[];
  readonly blocksMovement: boolean;
  readonly oneVisitPerHero: boolean;
  readonly weeklyReset: boolean;
  readonly payload: Readonly<Record<string, number | string | boolean | null>>;
}

// ------------------------------------------------------------ terrain / level

export interface TerrainDef {
  readonly id: TerrainKind;
  readonly moveCost: number;
  readonly impassable: boolean;
  readonly water: boolean;
  readonly battlefield: string;
  readonly priority: number;
}

/**
 * Section 31 - magic ground.
 *
 * The classic's nine special battlefields are patches of ground that change the
 * battle fought on them: Ley Plains casts every spell as an expert would,
 * Hexed Soil allows none above the first rung, the four elemental fields
 * favour one school, and Blessed Earth, Gloom Mist and Lucky Meadow move morale
 * and luck. They are not terrain kinds here: a terrain is a movement cost and a
 * tile set with a transition to every neighbour, and magic ground is a *wash*
 * over whatever ground it lies on, which is also how it reads on the classic's map.
 *
 * What each one does is authored, not coded. `modifiers` is the field list -
 * installed on every stack on BOTH sides, the same scope `Oathbreaker's Cloak`
 * uses - so a new ground is a data edit and the kernel never names one.
 * `creatureModifiers` is the second half, keyed by an ability rather than by a
 * creature, which is what lets Blessed Earth bless the living and curse the dead
 * in one entry.
 */
export interface MagicGroundDef {
  readonly id: string;
  readonly name: string;
  /** The painted arena this ground is fought on (`battlefield.<id>`). */
  readonly battlefield: string;
  /** The wash the world map draws over the ground, `#rrggbb`. */
  readonly tint: string;
  /** Alpha of that wash in basis points, so the ground under it still reads. */
  readonly tintBp: number;
  /** Installed on every stack on both sides. */
  readonly modifiers: readonly Modifier[];
  /** Installed only on stacks carrying (or not carrying) one ability. */
  readonly abilityModifiers: readonly {
    readonly ability: string;
    /** True: stacks WITH the ability. False: stacks without it. */
    readonly present: boolean;
    readonly modifiers: readonly Modifier[];
  }[];
  readonly description: string;
}

export interface LevelCurve {
  /** Experience needed to reach index+1; index 0 is level 1. */
  readonly thresholds: readonly number[];
  /** Chance in basis points of each primary skill, by might/magic class. */
  readonly primaryChance: Readonly<Record<string, readonly [number, number, number, number]>>;
  /**
   * Relative weights for secondary-skill offers, by hero class then skill id.
   * A missing class falls back to a uniform draw over every unheld skill; a
   * listed class treats an unlisted skill as banned (weight 0) for new offers.
   * Absent entirely means every class is uniform - testdata can omit it.
   */
  readonly secondaryChance?: Readonly<Record<string, Readonly<Record<string, number>>>>;
  /**
   * What a hero's CLASS is worth, on top of what his level and skills are.
   *
   * `HeroDef.heroClass` has existed since the heroes were authored - twenty-six
   * of them across 215 heroes, Knight through Gill-Priest - and drove exactly
   * two things: the odds of each primary on level-up, and which secondary
   * skills get offered. Both are statistical. Two Knights and two Warlocks
   * played identically on the day they were hired, and a class was a name on a
   * sheet.
   *
   * This is the class said out loud: a standing modifier list every hero of
   * that class carries, merged in `specialtyModifiers` beside the hero's own.
   * It is deliberately NOT the Heroes IV design, where a class is earned by
   * combining two advanced skills - that fights a table of 215 heroes each of
   * which already names its class, and it would make the first forty levels of
   * every hero identical again. Earned titles can be a second axis later; this
   * one costs a data table and pays out on turn one.
   *
   * Optional, and an unlisted class carries nothing, so testdata may omit it.
   */
  readonly classPassive?: Readonly<Record<string, readonly Modifier[]>>;
}

// ------------------------------------------------------------------ registry

export interface ContentRegistry {
  readonly contentVersion: string;
  readonly creatures: ReadonlyMap<CreatureDefId, CreatureDef>;
  readonly spells: ReadonlyMap<SpellDefId, SpellDef>;
  readonly artifacts: ReadonlyMap<ArtifactDefId, ArtifactDef>;
  readonly skills: ReadonlyMap<SkillDefId, SecondarySkillDef>;
  readonly buildings: ReadonlyMap<BuildingDefId, BuildingDef>;
  readonly heroes: ReadonlyMap<HeroDefId, HeroDef>;
  readonly factions: ReadonlyMap<FactionId, FactionDef>;
  readonly mapObjects: ReadonlyMap<string, MapObjectDef>;
  readonly terrain: ReadonlyMap<TerrainKind, TerrainDef>;
  /** Section 31. Empty in a content set that ships no magic ground. */
  readonly magicGrounds: ReadonlyMap<string, MagicGroundDef>;
  readonly levels: LevelCurve;
  readonly resourceValue: Readonly<Record<ResourceKind, number>>;
  readonly stats: Readonly<Record<string, number>>;
}

/** Lookup that throws only on a content bug, never on a player action. */
export function must<K, V>(m: ReadonlyMap<K, V>, k: K, what: string): V {
  const v = m.get(k);
  if (v === undefined) throw new Error(`content bug: no ${what} "${String(k)}"`);
  return v;
}

export type { StatKey, ResourceKind };
